


















































































































import { computed, defineComponent, nextTick, PropType, ref, watch } from '@vue/composition-api';
import { ExclamationCircleIcon, PlusIcon } from '@vue-hero-icons/solid';
import { TriggerCondition, Workflow, WorkflowTrigger } from '../../types';
import { TriggerDataLoadedCondition, TriggerPipelineExecutedCondition, TriggerSummary } from '.';
import { ConfirmButton, Scrollbar } from '@/app/components';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { clone, invertObj } from 'ramda';
import {
    TriggerDataType,
    TriggerConditionType,
    TRIGGER_MAP,
    TRIGGER_PIPELINE_STATUS_MAP,
    TTriggerType,
} from '../../constants';
import { CreateTriggerDTO, UpdateTriggerDTO } from '../../dto/trigger.dto';
import { XCircleIcon } from '@vue-hero-icons/solid';
import { Asset } from '@/modules/asset/types';
import { useWorkflowTriggers } from '../../composable';

export default defineComponent({
    name: 'TriggerEditor',
    components: {
        ValidationProvider,
        TriggerDataLoadedCondition,
        TriggerPipelineExecutedCondition,
        ValidationObserver,
        PlusIcon,
        Scrollbar,
        XCircleIcon,
        ExclamationCircleIcon,
        ConfirmButton,
        TriggerSummary,
    },
    model: { prop: 'trigger', event: 'change' },
    props: {
        workflow: {
            type: Object as PropType<Workflow>,
            required: true,
        },
        trigger: {
            type: Object as PropType<WorkflowTrigger>,
        },
        saving: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, { emit }: { emit: any; root: any }) {
        const workflow = computed(() => props.workflow);
        const { getTriggerSummary } = useWorkflowTriggers(workflow, false);

        const conditions = ref<TriggerCondition[]>([]);
        const triggerRef = ref<any>(null);
        const editMode = ref<boolean>(false);
        const initialConditions = ref<string | null>(null);
        const summary = ref<string[]>([]);
        const loading = ref<boolean>(false);

        const assets = ref<Asset[]>([]);
        const pipelines = ref<Workflow[]>([]);

        const conditionTypes = [
            {
                id: TriggerConditionType.DataLoaded,
                name: 'When new data arrive',
            },
            {
                id: TriggerConditionType.PipelineExecuted,
                name: 'When another pipeline has been executed',
            },
        ];

        const addCondition = () =>
            conditions.value.push({
                type: null,
                entityId: null,
                entityName: null,
                entityType: null,
                entityTypeId: null,
                checks: {},
            } as TriggerCondition);

        const cancelTrigger = () => emit('cancel', hasChanges.value);

        const saveTrigger = async () => {
            if (!triggerRef.value) return;

            const valid = await triggerRef.value.validate();

            if (valid) {
                const data = clone(conditions.value);

                if (props.trigger && editMode.value) {
                    const updatePayload: UpdateTriggerDTO = {
                        id: props.trigger.id,
                        conditions: data.map((condition: TriggerCondition) => mapConditionForServer(condition)),
                        configuration: {},
                    };
                    emit('save', updatePayload);
                } else {
                    const createPayload: CreateTriggerDTO = {
                        workflowId: props.workflow.id,
                        isEnabled: !props.disabled,
                        conditions: data.map((condition: TriggerCondition) => mapConditionForServer(condition)),
                        configuration: {},
                    };
                    emit('save', createPayload);
                }
            }
        };

        const mapConditionForServer = (condition: TriggerCondition) => {
            if (condition.entityTypeId) {
                condition.type = condition.entityTypeId as TTriggerType;
                delete condition.entityTypeId;
            }

            if (condition.checks.status) {
                condition.checks.status = TRIGGER_PIPELINE_STATUS_MAP[condition.checks.status];
            }

            mapConditionChecksForServer(condition.checks.filters);
            return condition;
        };

        const mapConditionChecksForServer = (checks: any[]) => {
            checks?.map(
                (c: any) =>
                    (c.type = [TriggerDataType.Double, TriggerDataType.Integer].includes(c.type) ? 'number' : c.type),
            );
            return checks;
        };

        const handleConditionChange = (condition: TriggerCondition) => {
            condition.entityId = null;
            condition.entityName = null;
            condition.entityType = null;
            condition.entityTypeId = null;
            condition.checks = {};
        };

        const updateTrigger = (trigger: any) => {
            const clonedTrigger = clone(trigger);
            if (clonedTrigger) {
                conditions.value = clonedTrigger.conditions.map((condition: any) => ({
                    ...condition,
                    entityTypeId: condition.type,
                    type: TRIGGER_MAP[condition.type],
                    checks: {
                        ...condition.checks,
                        ...(condition.checks.status
                            ? { status: invertObj(TRIGGER_PIPELINE_STATUS_MAP)[condition.checks.status] }
                            : {}),
                    },
                }));
                initialConditions.value = JSON.stringify(conditions.value);
                editMode.value = true;
            } else {
                conditions.value = [];

                addCondition();
                editMode.value = false;
            }
        };

        const saveButtonLabel: any = computed(() =>
            editMode.value ? `Updat${props.saving ? 'ing' : 'e'}` : `Sav${props.saving ? 'ing' : 'e'}`,
        );

        const removeCondition = (_: TriggerCondition, index: number) => conditions.value.splice(index, 1);

        const hasChanges = computed(() => {
            const clonedConditions = clone(conditions.value).map((condition: TriggerCondition) => {
                mapConditionChecksForServer(condition.checks.filters);
                return condition;
            });

            return initialConditions.value && initialConditions.value !== JSON.stringify(clonedConditions);
        });

        const getHasChanges = () => hasChanges.value;

        const otherPipelineIds = (idx: number) => {
            return conditions.value
                .filter((cond, i) => i !== idx && cond.type === TriggerConditionType.PipelineExecuted)
                .map((cond) => cond.entityId);
        };

        const setPipelines = (pipeline: Workflow) => {
            pipelines.value = [...pipelines.value, pipeline];
        };

        watch(
            () => [props.trigger, triggerRef.value],
            ([trigger, observer]: any) => {
                updateTrigger(trigger);
                if (observer) {
                    nextTick(() => {
                        if (trigger) observer.validate();
                        else observer.reset();
                    });
                }
            },
            { immediate: true },
        );

        watch(
            () => [conditions.value, assets.value, pipelines.value],
            () => {
                summary.value = getTriggerSummary(conditions.value, assets.value, pipelines.value);
            },
            { deep: true },
        );

        return {
            assets,
            loading,
            TriggerConditionType,
            conditionTypes,
            triggerRef,
            conditions,
            addCondition,
            saveTrigger,
            cancelTrigger,
            editMode,
            handleConditionChange,
            saveButtonLabel,
            removeCondition,
            hasChanges,
            getHasChanges,
            summary,
            pipelines,
            initialConditions,
            otherPipelineIds,
            setPipelines,
        };
    },
});
